import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Save, User, Mail, Phone, Calendar, MapPin,
  Building, Heart, Briefcase, Award, FileText, Pencil, Trash2
} from 'lucide-react';

const TITLES = ["Principal", "Vice Principal", "Head Master", "Head of Department", "Senior Teacher", "Teacher", "Lab Assistant", "Librarian", "Counselor", "Administrative Staff"];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const DEPARTMENTS = ["Science", "Mathematics", "English", "Hindi", "Social Studies", "Computer Science", "Physical Education", "Arts", "Music", "Administration", "Library"];

const Section = ({ title, icon: Icon, children }) => (
  <div className="glass-card p-5">
    <div className="flex items-center gap-2 mb-4">
      <Icon className="h-4 w-4 text-teal-500" />
      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">{title}</h3>
    </div>
    {children}
  </div>
);

const Field = ({ label, value, field, type = 'text', options, editing, form, setForm }) => {
  if (!editing) return (
    <div>
      <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-sm text-slate-700 font-medium">{value || <span className="text-slate-300 italic">Not set</span>}</p>
    </div>
  );
  if (options) return (
    <div className="space-y-1">
      <Label className="text-[10px] text-slate-500 uppercase">{label}</Label>
      <Select value={form[field] || ''} onValueChange={(v) => setForm({ ...form, [field]: v })}>
        <SelectTrigger className="bg-white border-slate-200 text-slate-800 rounded-lg h-9 text-sm"><SelectValue placeholder={`Select ${label}`} /></SelectTrigger>
        <SelectContent className="bg-white border-slate-200 text-slate-800">{options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
  return (
    <div className="space-y-1">
      <Label className="text-[10px] text-slate-500 uppercase">{label}</Label>
      <Input type={type} value={form[field] || ''} onChange={(e) => setForm({ ...form, [field]: e.target.value })} className="bg-white border-slate-200 text-slate-800 rounded-lg h-9 text-sm" />
    </div>
  );
};

export default function EmployeeProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const isAdmin = currentUser?.role === 'school_admin';
  const isSelf = currentUser?.user_id === userId;
  const canEdit = isAdmin || isSelf;
  const [form, setForm] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/employees/${userId}`);
        setEmp(res.data.data);
        setForm(res.data.data);
        if (res.data.data.photo_path) {
          try {
            const pr = await api.get(`/files/${res.data.data.photo_path}`, { responseType: 'blob' });
            setPhotoUrl(URL.createObjectURL(pr.data));
          } catch {}
        }
      } catch { toast.error('Failed to load profile'); navigate('/dashboard/employees'); }
      finally { setLoading(false); }
    })();
  }, [userId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      const fields = ['full_name','title','department','employee_id','phone','date_of_birth','date_of_joining','salary','address','city','state','pin_code','emergency_contact_name','emergency_contact_phone','emergency_contact_relation','blood_group','bio','gender','marital_status','status'];
      fields.forEach(f => { if (form[f] !== emp[f]) updates[f] = form[f]; });
      if (JSON.stringify(form.previous_experience) !== JSON.stringify(emp.previous_experience)) updates.previous_experience = form.previous_experience;
      if (JSON.stringify(form.certifications) !== JSON.stringify(emp.certifications)) updates.certifications = form.certifications;
      if (Object.keys(updates).length === 0) { toast.info('No changes'); setEditing(false); setSaving(false); return; }
      await api.patch(`/employees/${userId}`, updates);
      toast.success('Profile updated');
      setEmp({ ...emp, ...updates });
      setEditing(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${emp.full_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted');
      navigate('/dashboard/employees');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await api.post(`/users/${userId}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo uploaded');
      const res = await api.get(`/employees/${userId}`);
      if (res.data.data.photo_path) {
        const pr = await api.get(`/files/${res.data.data.photo_path}`, { responseType: 'blob' });
        setPhotoUrl(URL.createObjectURL(pr.data));
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploadingPhoto(false); }
  };

  const addExperience = () => setForm({ ...form, previous_experience: [...(form.previous_experience || []), { organization: '', role: '', years: '', details: '' }] });
  const addCertification = () => setForm({ ...form, certifications: [...(form.certifications || []), { name: '', issuer: '', year: '' }] });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="h-8 w-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" /></div>;
  if (!emp) return null;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="employee-profile">
      <div className="flex items-center justify-between">
        <Button variant="ghost" className="text-slate-500 hover:text-slate-800" onClick={() => navigate('/dashboard/employees')} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Directory
        </Button>
        {canEdit && !editing && (
          <div className="flex gap-2">
            {isAdmin && !isSelf && (
              <Button variant="outline" className="border-red-200 text-red-500 hover:bg-red-50 rounded-xl" onClick={handleDelete}>
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </Button>
            )}
            <Button className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl shadow-sm" onClick={() => setEditing(true)} data-testid="edit-btn">
              <Pencil className="h-4 w-4 mr-2" /> Edit Profile
            </Button>
          </div>
        )}
        {editing && (
          <div className="flex gap-2">
            <Button variant="outline" className="border-slate-200 text-slate-600 rounded-xl" onClick={() => { setEditing(false); setForm(emp); }}>Cancel</Button>
            <Button className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl" onClick={handleSave} disabled={saving} data-testid="save-btn">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      {/* Header Card */}
      <div className="glass-card p-6">
        <div className="flex items-start gap-6 flex-wrap">
          <div className="relative">
            {photoUrl ? (
              <img src={photoUrl} alt={emp.full_name} className="h-24 w-24 rounded-2xl object-cover border-2 border-slate-200 shadow-sm" />
            ) : (
              <div className="h-24 w-24 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-md">
                <span className="text-3xl font-bold text-white">{emp.full_name?.charAt(0)}</span>
              </div>
            )}
            {canEdit && (
              <button onClick={() => fileInputRef.current?.click()} className="absolute -bottom-2 -right-2 h-8 w-8 rounded-lg bg-teal-500 flex items-center justify-center text-white hover:bg-teal-400 transition-colors shadow-sm">
                {uploadingPhoto ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Upload className="h-4 w-4" />}
              </button>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
          </div>
          <div className="flex-1">
            {editing ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Full Name" field="full_name" value={emp.full_name} editing={editing} form={form} setForm={setForm} />
                <Field label="Title" field="title" value={emp.title} options={TITLES} editing={editing} form={form} setForm={setForm} />
                <Field label="Employee ID" field="employee_id" value={emp.employee_id} editing={editing} form={form} setForm={setForm} />
                <Field label="Phone" field="phone" value={emp.phone} editing={editing} form={form} setForm={setForm} />
                <Field label="Department" field="department" value={emp.department} options={DEPARTMENTS} editing={editing} form={form} setForm={setForm} />
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-slate-800" data-testid="emp-name">{emp.full_name}</h2>
                <p className="text-teal-600 text-sm font-semibold">{emp.title || 'No title'} {emp.employee_id ? `(${emp.employee_id})` : ''}</p>
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  <span className="flex items-center gap-1.5 text-xs text-slate-500"><Mail className="h-3 w-3 text-slate-400" />{emp.email}</span>
                  {emp.phone && <span className="flex items-center gap-1.5 text-xs text-slate-500"><Phone className="h-3 w-3 text-slate-400" />{emp.phone}</span>}
                  {emp.department && <span className="flex items-center gap-1.5 text-xs text-slate-500"><Building className="h-3 w-3 text-slate-400" />{emp.department}</span>}
                  {emp.years_of_service != null && <span className="flex items-center gap-1.5 text-xs text-slate-500"><Calendar className="h-3 w-3 text-slate-400" />{emp.years_of_service} years</span>}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Personal Information" icon={User}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Date of Birth" field="date_of_birth" value={emp.date_of_birth} type="date" editing={editing} form={form} setForm={setForm} />
            <Field label="Gender" field="gender" value={emp.gender} options={['Male','Female','Other']} editing={editing} form={form} setForm={setForm} />
            <Field label="Blood Group" field="blood_group" value={emp.blood_group} options={BLOOD_GROUPS} editing={editing} form={form} setForm={setForm} />
            <Field label="Marital Status" field="marital_status" value={emp.marital_status} options={['Single','Married','Divorced','Widowed']} editing={editing} form={form} setForm={setForm} />
            <Field label="Phone" field="phone" value={emp.phone} editing={editing} form={form} setForm={setForm} />
          </div>
        </Section>
        <Section title="Employment Details" icon={Briefcase}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Date of Joining" field="date_of_joining" value={emp.date_of_joining} type="date" editing={editing} form={form} setForm={setForm} />
            <Field label="Role" field="role" value={emp.role?.replace('_', ' ')} editing={editing} form={form} setForm={setForm} />
            <Field label="Salary (Monthly)" field="salary" value={emp.salary ? `Rs.${Number(emp.salary).toLocaleString('en-IN')}` : null} type="number" editing={editing} form={form} setForm={setForm} />
            <Field label="Department" field="department" value={emp.department} editing={editing} form={form} setForm={setForm} />
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Status</p>
              {editing ? (
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, status: f.status === 'active' ? 'inactive' : 'active' }))}
                  className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-bold transition-colors ${form.status === 'active' ? 'bg-emerald-100 text-emerald-700 hover:bg-rose-100 hover:text-rose-700' : 'bg-rose-100 text-rose-700 hover:bg-emerald-100 hover:text-emerald-700'}`}
                >
                  {form.status === 'active' ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                </button>
              ) : (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold ${emp.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{emp.status}</span>
              )}
            </div>
          </div>
        </Section>
        <Section title="Address" icon={MapPin}>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Field label="Address" field="address" value={emp.address} editing={editing} form={form} setForm={setForm} /></div>
            <Field label="City" field="city" value={emp.city} editing={editing} form={form} setForm={setForm} />
            <Field label="State" field="state" value={emp.state} editing={editing} form={form} setForm={setForm} />
            <Field label="PIN Code" field="pin_code" value={emp.pin_code} editing={editing} form={form} setForm={setForm} />
          </div>
        </Section>
        <Section title="Emergency Contact" icon={Heart}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Contact Name" field="emergency_contact_name" value={emp.emergency_contact_name} editing={editing} form={form} setForm={setForm} />
            <Field label="Contact Phone" field="emergency_contact_phone" value={emp.emergency_contact_phone} editing={editing} form={form} setForm={setForm} />
            <Field label="Relationship" field="emergency_contact_relation" value={emp.emergency_contact_relation} editing={editing} form={form} setForm={setForm} />
          </div>
        </Section>
      </div>

      <Section title="Bio" icon={FileText}>
        {editing ? (
          <textarea value={form.bio || ''} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} className="w-full bg-white border border-slate-200 rounded-lg text-slate-800 text-sm p-3 placeholder:text-slate-300" placeholder="Brief bio..." />
        ) : (
          <p className="text-sm text-slate-600 leading-relaxed">{emp.bio || <span className="italic text-slate-300">No bio added</span>}</p>
        )}
      </Section>

      <Section title="Previous Experience" icon={Briefcase}>
        {(form.previous_experience || []).map((exp, i) => (
          <div key={i} className="p-3 rounded-xl bg-slate-50 border border-slate-100 mb-3">
            {editing ? (
              <div className="grid grid-cols-2 gap-2">
                <Input value={exp.organization} placeholder="Organization" onChange={(e) => { const arr = [...form.previous_experience]; arr[i].organization = e.target.value; setForm({ ...form, previous_experience: arr }); }} className="bg-white border-slate-200 text-slate-800 text-sm rounded-lg h-8" />
                <Input value={exp.role} placeholder="Role/Position" onChange={(e) => { const arr = [...form.previous_experience]; arr[i].role = e.target.value; setForm({ ...form, previous_experience: arr }); }} className="bg-white border-slate-200 text-slate-800 text-sm rounded-lg h-8" />
                <Input value={exp.years} placeholder="Duration" onChange={(e) => { const arr = [...form.previous_experience]; arr[i].years = e.target.value; setForm({ ...form, previous_experience: arr }); }} className="bg-white border-slate-200 text-slate-800 text-sm rounded-lg h-8" />
                <Button size="sm" variant="ghost" className="text-rose-500 h-8" onClick={() => { const arr = form.previous_experience.filter((_, j) => j !== i); setForm({ ...form, previous_experience: arr }); }}>Remove</Button>
              </div>
            ) : (
              <div>
                <p className="text-sm text-slate-700 font-semibold">{exp.organization || 'Unnamed'}</p>
                <p className="text-xs text-slate-500">{exp.role} {exp.years ? `- ${exp.years}` : ''}</p>
              </div>
            )}
          </div>
        ))}
        {editing && <Button size="sm" variant="outline" className="border-slate-200 text-slate-600 rounded-lg" onClick={addExperience}>+ Add Experience</Button>}
        {!editing && (!emp.previous_experience || emp.previous_experience.length === 0) && <p className="text-sm text-slate-300 italic">No experience added</p>}
      </Section>

      <Section title="Certifications" icon={Award}>
        {(form.certifications || []).map((cert, i) => (
          <div key={i} className="p-3 rounded-xl bg-slate-50 border border-slate-100 mb-3">
            {editing ? (
              <div className="grid grid-cols-3 gap-2">
                <Input value={cert.name} placeholder="Name" onChange={(e) => { const arr = [...form.certifications]; arr[i].name = e.target.value; setForm({ ...form, certifications: arr }); }} className="bg-white border-slate-200 text-slate-800 text-sm rounded-lg h-8" />
                <Input value={cert.issuer} placeholder="Issuing Body" onChange={(e) => { const arr = [...form.certifications]; arr[i].issuer = e.target.value; setForm({ ...form, certifications: arr }); }} className="bg-white border-slate-200 text-slate-800 text-sm rounded-lg h-8" />
                <div className="flex gap-2">
                  <Input value={cert.year} placeholder="Year" onChange={(e) => { const arr = [...form.certifications]; arr[i].year = e.target.value; setForm({ ...form, certifications: arr }); }} className="bg-white border-slate-200 text-slate-800 text-sm rounded-lg h-8" />
                  <Button size="sm" variant="ghost" className="text-rose-500 h-8 px-2" onClick={() => { const arr = form.certifications.filter((_, j) => j !== i); setForm({ ...form, certifications: arr }); }}>X</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-slate-700 font-semibold">{cert.name}</p><p className="text-xs text-slate-500">{cert.issuer} {cert.year ? `(${cert.year})` : ''}</p></div>
                <Award className="h-4 w-4 text-amber-400" />
              </div>
            )}
          </div>
        ))}
        {editing && <Button size="sm" variant="outline" className="border-slate-200 text-slate-600 rounded-lg" onClick={addCertification}>+ Add Certification</Button>}
        {!editing && (!emp.certifications || emp.certifications.length === 0) && <p className="text-sm text-slate-300 italic">No certifications added</p>}
      </Section>
    </div>
  );
}
